import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Flex, Input, message, Typography } from 'antd';
import { PartitionOutlined, SearchOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import InstallationLocationTable, {
  LocationTableRow,
} from '../../components/installation-location/InstallationLocationTable';
import './InstallationLocationPage.scss';
import { useTranslation } from '../../i18n/i18n';
import CreateInstallationLocationModal, {
  CreateLocationParams,
} from '../../components/installation-location/InstallationLocationDetail/CreateInstallationLocationModal';
import { locationInstallationDetailPath } from '../../routes';
import { locationApi } from '../../api/location.api';
import { installationApi } from '../../api/installation.api';
import { tenantApi } from '../../api/tenant.api';
import useAuthStore from '../../stores/auth.store';
import useTenantStore, { Tenant } from '../../stores/tenant.store';
import { userApi } from '../../api/user.api';
import { ADAPTIVE_TABLE_CONTAINER_CLASS, useAdaptivePageSize } from '../../hooks/useAdaptivePageSize';

const { Title } = Typography;

const FIRST_PAGE = 1;

async function resolveTenantLabels(locs: { tenantUuid: string }[], tenants: Tenant[]): Promise<Map<string, string>> {
  const missingUuids = Array.from(
    new Set(
      locs
        .map((loc) => loc.tenantUuid)
        .filter((uuid): uuid is string => !!uuid && !tenants.some((t) => t.value === uuid)),
    ),
  );
  if (missingUuids.length === 0) return new Map();
  const fetched = await Promise.all(missingUuids.map((uuid) => tenantApi.getTenantByUuid(uuid).catch(() => null)));
  return new Map(fetched.filter((t): t is NonNullable<typeof t> => !!t).map((t) => [t.uuid, t.name]));
}

function InstallationLocationPage() {
  const { t } = useTranslation('installation-location');
  const navigate = useNavigate();
  const currentPath = useLocation();
  const [messageApi, contextHolder] = message.useMessage();
  const [locations, setLocations] = useState<LocationTableRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [address, setAddress] = useState('');
  const [erpId, setErpId] = useState('');
  const isSuperAdminOrCompanyAdminOrAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdminOrAdmin);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const selectedTenant = useTenantStore((state) => state.selectedTenant);
  const tenants = useTenantStore((state) => state.tenants);
  const {
    pageSize,
    containerRef: tableContainerRef,
    containerHeight,
    recalculate,
  } = useAdaptivePageSize();
  const latestRequestIdRef = useRef(0);

  function onRowClick(record: LocationTableRow) {
    const path = currentPath.pathname.split('/').slice(0, 2).join('/');
    navigate(`${path}/${locationInstallationDetailPath}/${record.uuid}`);
  }

  const fetchLocations = useCallback(
    async (pageIndex: number, size: number) => {
      const requestId = ++latestRequestIdRef.current;
      setLoading(true);
      try {
        const response = await locationApi.getListByPage(pageIndex, size);
        const installationCounts = await Promise.all(
          response.locations.map((loc) => installationApi.getInstallationByLocationUuid(loc.uuid)),
        );
        const uniqueAdminUuids = Array.from(
          new Set(response.locations.map((loc) => loc.adminUuid).filter((uuid): uuid is string => !!uuid)),
        );
        const adminUsers = await Promise.all(
          uniqueAdminUuids.map((uuid) => userApi.getUserByUuid(uuid).catch(() => null)),
        );
        const adminMap = new Map(adminUsers.filter((u): u is NonNullable<typeof u> => !!u).map((u) => [u.uuid, u]));
        const tenantMap = await resolveTenantLabels(response.locations, tenants);
        const mapped = response.locations.map((loc, i) => ({
          uuid: loc.uuid,
          name: loc.name,
          erpId: loc.erpId || loc.uuid,
          direction: loc.address,
          poblation: loc.city,
          installations: String(installationCounts[i].length),
          devices: [],
          tenant: tenants.find((t) => t.value === loc.tenantUuid)?.label ?? tenantMap.get(loc.tenantUuid) ?? loc.tenantUuid,
          admin: loc.adminUuid ? (adminMap.get(loc.adminUuid)?.name ?? '-') : '-',
        }));
        if (latestRequestIdRef.current !== requestId) return;
        setLocations(mapped);
        setTotal(response.metadata?.totalCount ?? 0);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      } finally {
        if (latestRequestIdRef.current === requestId) setLoading(false);
      }
    },
    [tenants],
  );

  useEffect(() => {
    setPage(FIRST_PAGE);
  }, [selectedTenant, pageSize]);

  useEffect(() => {
    if (isSearchActive) return;
    fetchLocations(page, pageSize);
  }, [page, pageSize, selectedTenant, fetchLocations, isSearchActive]);

  useLayoutEffect(() => {
    recalculate();
  }, [total, recalculate]);

  const fetchSearchResults = useCallback(
    async (pageIndex: number, size: number) => {
      const requestId = ++latestRequestIdRef.current;
      const previousPage = page;
      setPage(pageIndex);
      setLoading(true);
      try {
        const [addressResponse, erpIdResponse] = await Promise.all([
          address ? locationApi.getListByAddress(address, pageIndex, size) : Promise.resolve(null),
          erpId ? locationApi.getListByERPId(erpId, pageIndex, size) : Promise.resolve(null),
        ]);
        const byAddress = addressResponse?.locations ?? [];
        const byERPId = erpIdResponse?.locations ?? [];
        const deduped = [...byAddress, ...byERPId].filter(
          (loc, i, arr) => arr.findIndex((l) => l.uuid === loc.uuid) === i,
        );
        const installationCounts = await Promise.all(
          deduped.map((loc) => installationApi.getInstallationByLocationUuid(loc.uuid)),
        );
        const uniqueAdminUuids = Array.from(
          new Set(deduped.map((loc) => loc.adminUuid).filter((uuid): uuid is string => !!uuid)),
        );
        const adminUsers = await Promise.all(
          uniqueAdminUuids.map((uuid) => userApi.getUserByUuid(uuid).catch(() => null)),
        );
        const adminMap = new Map(adminUsers.filter((u): u is NonNullable<typeof u> => !!u).map((u) => [u.uuid, u]));
        const tenantMap = await resolveTenantLabels(deduped, tenants);
        const merged = deduped.map((loc, i) => ({
          uuid: loc.uuid,
          name: loc.name,
          erpId: loc.erpId || loc.uuid,
          direction: loc.address,
          poblation: loc.city,
          installations: String(installationCounts[i].length),
          devices: [],
          tenant: tenants.find((t) => t.value === loc.tenantUuid)?.label ?? tenantMap.get(loc.tenantUuid) ?? loc.tenantUuid,
          admin: loc.adminUuid ? (adminMap.get(loc.adminUuid)?.name ?? '-') : '-',
        }));
        if (latestRequestIdRef.current !== requestId) return;
        setLocations(merged);
        setTotal(Math.max(addressResponse?.metadata?.totalCount ?? 0, erpIdResponse?.metadata?.totalCount ?? 0));
      } catch (error) {
        console.error('Failed to fetch locations:', error);
        if (latestRequestIdRef.current === requestId) setPage(previousPage);
      } finally {
        if (latestRequestIdRef.current === requestId) setLoading(false);
      }
    },
    [address, erpId, tenants, page],
  );

  const handleSearchButton = () => {
    if (address === '' && erpId === '') {
      setIsSearchActive(false);
      setPage(FIRST_PAGE);
      fetchLocations(FIRST_PAGE, pageSize);
      return;
    }
    setIsSearchActive(true);
    fetchSearchResults(FIRST_PAGE, pageSize);
  };

  const handleCreate = async (values: CreateLocationParams) => {
    await locationApi.create(values);
    await fetchLocations(page, pageSize);
  };

  const handleDelete = async (location: LocationTableRow) => {
    try {
      await locationApi.delete(location.uuid);
      const newPage = locations.length === 1 && page > 1 ? page - 1 : page;
      if (newPage !== page) setPage(newPage);
      await fetchLocations(newPage, pageSize);
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    }
  };

  return (
    <Flex vertical>
      {contextHolder}
      <br />
      <div className={'InstallationLocationPage__header'}>
        <div className={'InstallationLocationPage__header__title'}>
          <PartitionOutlined className={'InstallationLocationPage__icon'} />
          <Title level={1} className={'InstallationLocationPage__title'}>
            {t('title')}
          </Title>
        </div>
        {isSuperAdminOrCompanyAdminOrAdmin && <CreateInstallationLocationModal onSave={handleCreate} />}
      </div>
      <br />
      <div className={'InstallationLocationPage__searchSection'}>
        <Input
          className={'InstallationLocationPage__searchSection__direction'}
          placeholder={t('search_direction')}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onPressEnter={handleSearchButton}
        />
        <Input
          className={'InstallationLocationPage__searchSection__id'}
          placeholder={t('search_id')}
          value={erpId}
          onChange={(e) => setErpId(e.target.value)}
          onPressEnter={handleSearchButton}
        />

        <Button onClick={handleSearchButton}>
          <SearchOutlined /> {t('search')}
        </Button>
      </div>
      <div
        className={`InstallationLocationPage__table ${ADAPTIVE_TABLE_CONTAINER_CLASS}`}
        style={{ height: containerHeight }}
        ref={tableContainerRef}
      >
        <InstallationLocationTable
          data={locations}
          pagination={{ pageSize, total, current: page, showSizeChanger: false }}
          onChange={(pagination) => {
            const newPage = pagination.current ?? 1;
            if (isSearchActive) {
              fetchSearchResults(newPage, pageSize);
            } else {
              setPage(newPage);
              fetchLocations(newPage, pageSize);
            }
          }}
          onRowClick={onRowClick}
          handleClick={handleDelete}
          loading={loading}
        />
      </div>
    </Flex>
  );
}

export default InstallationLocationPage;
